import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const REASONS = ["Vendita", "Acquisto", "Locazione", "Consulenza"];
const TIPOLOGIE = ["Appartamento", "Attico", "Villa", "Loft", "Altro"];

export default function ContactForm({
  variant = "contatti",
  reason = "Vendita",
  property = null,
  testId = "contact-submit-button",
}) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    reason,
    message: "",
    tipo: "Appartamento",
    comune: "Milano",
    mq: "",
  });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        reason: variant === "visita" ? "Visita / Informazioni" : variant === "valutazione" ? reason : form.reason,
        message: form.message,
      };
      if (property) {
        payload.property_id = property.code;
        payload.property_title = property.title;
      }
      if (variant === "valutazione") {
        payload.context = `${form.tipo} · ${form.comune} · ${form.mq || "—"} mq`;
      }
      await api.post("/contacts", payload);
      toast.success("Richiesta inviata. Il nostro team vi ricontatterà al più presto.");
      setForm((f) => ({ ...f, message: "" }));
    } catch (err) {
      toast.error("Impossibile inviare la richiesta. Riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-7" data-testid={`${variant}-form`}>
      <div className="grid gap-7 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor={`${variant}-name`}>Nome e cognome *</label>
          <input
            id={`${variant}-name`}
            data-testid="contact-input-name"
            className="field-input"
            required
            value={form.name}
            onChange={set("name")}
            placeholder="Mario Rossi"
          />
        </div>
        <div>
          <label className="field-label" htmlFor={`${variant}-phone`}>Telefono *</label>
          <input
            id={`${variant}-phone`}
            data-testid="contact-input-phone"
            className="field-input"
            type="tel"
            required
            value={form.phone}
            onChange={set("phone")}
            placeholder="+39 333 000 0000"
          />
        </div>
      </div>

      <div>
        <label className="field-label" htmlFor={`${variant}-email`}>Email *</label>
        <input
          id={`${variant}-email`}
          data-testid="contact-input-email"
          className="field-input"
          type="email"
          required
          value={form.email}
          onChange={set("email")}
          placeholder="nome@email.it"
        />
      </div>

      {variant === "contatti" && (
        <div>
          <label className="field-label" htmlFor="contact-reason">Motivo della richiesta</label>
          <select
            id="contact-reason"
            data-testid="contact-select-reason"
            className="field-input cursor-pointer"
            value={form.reason}
            onChange={set("reason")}
          >
            {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      )}

      {variant === "valutazione" && (
        <div className="grid gap-7 sm:grid-cols-3">
          <div>
            <label className="field-label" htmlFor="val-tipo">Tipologia</label>
            <select id="val-tipo" className="field-input cursor-pointer" value={form.tipo} onChange={set("tipo")}>
              {TIPOLOGIE.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label" htmlFor="val-comune">Comune</label>
            <input id="val-comune" className="field-input" required value={form.comune} onChange={set("comune")} placeholder="Milano" />
          </div>
          <div>
            <label className="field-label" htmlFor="val-mq">Superficie (m²)</label>
            <input id="val-mq" className="field-input" type="number" min="10" value={form.mq} onChange={set("mq")} placeholder="120" />
          </div>
        </div>
      )}

      <div>
        <label className="field-label" htmlFor={`${variant}-message`}>Messaggio *</label>
        <textarea
          id={`${variant}-message`}
          data-testid="contact-input-message"
          className="field-input resize-none"
          rows={variant === "visita" ? 3 : 4}
          required
          value={form.message}
          onChange={set("message")}
          placeholder={
            variant === "visita"
              ? "Vorrei ricevere informazioni o prenotare una visita…"
              : variant === "valutazione"
                ? "Descrivete brevemente il vostro immobile…"
                : "Raccontateci il vostro progetto immobiliare…"
          }
        />
      </div>

      <button
        type="submit"
        data-testid={testId}
        disabled={sending}
        className="group inline-flex items-center gap-3 rounded-full bg-ink px-9 py-4 text-[12px] font-medium uppercase tracking-wide2 text-sand transition-all duration-300 hover:bg-champagne disabled:opacity-50"
      >
        {sending ? "Invio in corso…" : variant === "visita" ? "Richiedi informazioni" : variant === "valutazione" ? "Richiedi la valutazione" : "Invia la richiesta"}
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
}
